import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench, faHardHat, faCog, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from '../hooks/useTranslation';
import LanguageSwitcher from './LanguageSwitcher';
import '../styles/Header.css';

const Header = () => {
  const { t } = useTranslation();
  const [isConveyorBroken, setIsConveyorBroken] = useState(false);
  const [isWorkerFixing, setIsWorkerFixing] = useState(false);
  const [breakdownPosition, setBreakdownPosition] = useState(40);
  const [sparks, setSparks] = useState([]);
  const [smoke, setSmoke] = useState([]);
  const [flames, setFlames] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const conveyorRef = useRef(null);

  const workerIntervalRef = useRef(null);
  const moveAnimationRef = useRef(null);
  const [beltPosition, setBeltPosition] = useState(0);

  // KPI data state
  const [kpiData, setKpiData] = useState({
    efficiency: Math.floor(Math.random() * 30) + 70,
    temperature: Math.floor(Math.random() * 50) + 40,
    uptime: Math.floor(Math.random() * 10) + 90,
    production: Math.floor(Math.random() * 500) + 1500
  });

  useEffect(() => {
    const kpiInterval = setInterval(() => {
      if (!isConveyorBroken) {
        setKpiData({
          efficiency: Math.floor(Math.random() * 30) + 70,
          temperature: Math.floor(Math.random() * 50) + 40,
          uptime: Math.floor(Math.random() * 10) + 90,
          production: Math.floor(Math.random() * 500) + 1500
        });
      } else {
        setKpiData({
          efficiency: Math.floor(Math.random() * 10) + 10,
          temperature: Math.floor(Math.random() * 50) + 90,
          uptime: Math.floor(Math.random() * 30) + 20,
          production: Math.floor(Math.random() * 500) + 500
        });
      }
    }, 3000);
    return () => clearInterval(kpiInterval);
  }, [isConveyorBroken]);

  // Generate boxes
  useEffect(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const MAX_BOXES = isMobile ? 4 : 8;
    const spawnInterval = isMobile ? 4500 : 2500;

    const boxInterval = isConveyorBroken ? null : setInterval(() => {
      setBoxes(prev => {
        if (prev.length >= MAX_BOXES) return prev;
        const width = isMobile ? (Math.random() * 30 + 30) : (Math.random() * 50 + 40);
        const height = isMobile ? 25 : 40;

        const newBox = {
          id: Date.now(),
          color: ['#8B4513', '#A0522D', '#CD853F', '#D2691E'][Math.floor(Math.random() * 4)],
          width, height, hasTape: Math.random() > 0.3, rotation: Math.random() * 10 - 5, position: 110
        };
        return [...prev, newBox];
      });
    }, spawnInterval);

    const cleanupInterval = setInterval(() => {
      setBoxes(prev => prev.filter(box => box.position > -20));
    }, 500);

    return () => {
      if (boxInterval) clearInterval(boxInterval);
      if (cleanupInterval) clearInterval(cleanupInterval);
    };
  }, [isConveyorBroken]);

  // Movement Sync
  useEffect(() => {
    if (isConveyorBroken) return;
    const moveBoxes = () => {
      const speedPerFrame = 100 / (15 * 60);
      setBoxes(prevBoxes =>
        prevBoxes.map(box => ({ ...box, position: box.position - speedPerFrame }))
      );
      setBeltPosition(prev => (prev + 2.1) % 40);
      moveAnimationRef.current = requestAnimationFrame(moveBoxes);
    };
    moveAnimationRef.current = requestAnimationFrame(moveBoxes);
    return () => cancelAnimationFrame(moveAnimationRef.current);
  }, [isConveyorBroken]);

  // SIMPLIFIED EFFECTS - CENTERED
  useEffect(() => {
    if (!isConveyorBroken) return;
    const effectInterval = setInterval(() => {
      setSparks(prev => {
        const newSparks = [];
        for (let i = 0; i < 3; i++) {
          newSparks.push({
            id: Date.now() + i,
            duration: Math.random() * 0.8 + 0.4,
            size: Math.random() * 4 + 2,
            targetX: (Math.random() - 0.5) * 100,
            targetY: -(Math.random() * 80 + 20)
          });
        }
        return [...prev.slice(-20), ...newSparks];
      });

      setSmoke(prev => [...prev.slice(-10), {
        id: Date.now(),
        duration: Math.random() * 2.5 + 1.5,
        size: Math.random() * 50 + 30,
        opacity: Math.random() * 0.4 + 0.3
      }]);

      setFlames(prev => [...prev.slice(-8), {
        id: Date.now(),
        height: Math.random() * 50 + 30,
        width: Math.random() * 15 + 8,
        duration: Math.random() * 0.4 + 0.4
      }]);
    }, 200);
    return () => clearInterval(effectInterval);
  }, [isConveyorBroken]);

  const [workerPosition, setWorkerPosition] = useState(-100);
  const triggerBreakdown = useCallback(() => {
    const pos = Math.random() * 60 + 20;
    setBreakdownPosition(pos);
    setIsConveyorBroken(true);
    setWorkerPosition(-100);
    setIsWorkerFixing(true);
    if (workerIntervalRef.current) clearInterval(workerIntervalRef.current);

    setTimeout(() => {
      workerIntervalRef.current = setInterval(() => {
        setWorkerPosition(prev => {
          if (Math.abs(prev - pos) < 5) {
            setTimeout(() => {
              setIsConveyorBroken(false);
              setSparks([]); setSmoke([]); setFlames([]);
              setTimeout(() => {
                let leave = setInterval(() => {
                  setWorkerPosition(p => {
                    if (p > 115) { clearInterval(leave); setIsWorkerFixing(false); return -100; }
                    return p + 5;
                  });
                }, 100);
              }, 2000);
            }, 3000);
            return pos;
          }
          return prev < pos ? prev + 5 : prev - 5;
        });
      }, 100);
    }, 1000);
  }, []);

  useEffect(() => {
    const breakdownInterval = setInterval(() => triggerBreakdown(), 120000);
    const initialBreakdown = setTimeout(() => triggerBreakdown(), 5000);
    return () => {
      clearInterval(breakdownInterval); clearTimeout(initialBreakdown);
      if (workerIntervalRef.current) clearInterval(workerIntervalRef.current);
    };
  }, [triggerBreakdown]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <header className="header">
      <div className="conveyor-container">
        <div className={`conveyor ${isConveyorBroken ? 'broken' : ''}`} ref={conveyorRef}>
          <div className="conveyor-belt" style={{ backgroundPosition: `${beltPosition}px 0` }}></div>
          <div className="conveyor-rollers">
            {Array.from({ length: 12 }).map((_, i) => (<div key={i} className="roller"></div>))}
          </div>

          <div className="kpi-monitor">
            <div className="monitor-header">
              <div className="monitor-title">System Status</div>
              <div className={`status-indicator ${isConveyorBroken ? 'error' : 'normal'}`}></div>
            </div>
            <div className="monitor-content">
              <div className="kpi-row">
                <span>Efficiency:</span> <span className="kpi-value">{kpiData.efficiency}%</span>
                <span className={`kpi-arrow ${kpiData.efficiency > 65 ? 'up' : 'down'}`}>{kpiData.efficiency > 65 ? '▲' : '▼'}</span>
              </div>
              <div className="kpi-row">
                <span>Temperature:</span> <span className="kpi-value">{kpiData.temperature}°C</span>
                <span className={`kpi-arrow ${kpiData.temperature < 85 ? 'up' : 'down'}`}>{kpiData.temperature < 85 ? '▲' : '▼'}</span>
              </div>
              <div className="kpi-row">
                <span>Uptime:</span> <span className="kpi-value">{kpiData.uptime}%</span>
                <span className={`kpi-arrow ${kpiData.uptime > 75 ? 'up' : 'down'}`}>{kpiData.uptime > 75 ? '▲' : '▼'}</span>
              </div>
              <div className="kpi-row">
                <span>Production:</span> <span className="kpi-value">{kpiData.production}</span>
                <span className={`kpi-arrow ${kpiData.production > 1000 ? 'up' : 'down'}`}>{kpiData.production > 1000 ? '▲' : '▼'}</span>
              </div>
            </div>
          </div>

          <AnimatePresence>
            {boxes.map(box => (
              <motion.div key={box.id} className="box"
                style={{
                  right: `${box.position}%`, rotate: box.rotation, backgroundColor: box.color,
                  width: `${box.width}px`, height: `${box.height}px`,
                  bottom: isMobile ? '35px' : '45px',
                }}
              >
                {box.hasTape && <><div className="box-tape horizontal" style={{ height: isMobile ? '6px' : '10px' }}></div><div className="box-tape vertical" style={{ width: isMobile ? '6px' : '10px' }}></div></>}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* SIMPLIFIED CENTERED POINT */}
          <div className="breakdown-point" style={{ left: `${breakdownPosition}%`, position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)', width: '1px', height: '1px' }}>
            {sparks.map(spark => (
              <motion.div key={spark.id}
                initial={{ top: 0, left: 0, opacity: 1 }}
                animate={{ top: spark.targetY, left: spark.targetX, opacity: 0 }}
                transition={{ duration: spark.duration }}
                style={{ width: `${spark.size}px`, height: `${spark.size}px`, position: 'absolute', backgroundColor: '#ffcc00', borderRadius: '50%', boxShadow: '0 0 10px #ffcc00' }}
              />
            ))}
            {smoke.map(smokeItem => (
              <motion.div key={smokeItem.id}
                initial={{ top: 0, left: '50%', x: '-50%', opacity: smokeItem.opacity, scale: 0.2 }}
                animate={{ top: "-50px", opacity: 0, scale: 1.5 }}
                transition={{ duration: smokeItem.duration }}
                style={{ width: `${smokeItem.size}px`, height: `${smokeItem.size}px`, position: 'absolute', borderRadius: '50%', backgroundColor: 'rgba(150,150,150,0.4)', filter: 'blur(15px)' }}
              />
            ))}
            {flames.map(flame => (
              <motion.div key={flame.id}
                initial={{ height: 0, width: `${flame.width}px`, left: '50%', x: '-50%', opacity: 0.8 }}
                animate={{ height: `${flame.height}px`, opacity: 0 }}
                transition={{ duration: flame.duration }}
                style={{ position: 'absolute', bottom: '0', background: 'linear-gradient(to top, #ff9900, #ff0000)', borderRadius: '5px 5px 0 0', boxShadow: '0 0 20px #ff0000' }}
              />
            ))}
          </div>

          <AnimatePresence>
            {isWorkerFixing && (
              <motion.div className="worker" style={{ right: `${100 - workerPosition}%` }}>
                <div className="worker-body">
                  <FontAwesomeIcon icon={faHardHat} className="worker-helmet" />
                  <div className="worker-face"></div><div className="worker-torso"></div>
                  <motion.div className="worker-arm" animate={{ rotate: [0, -30, 0, -30, 0] }} transition={{ duration: 1, repeat: Infinity }}>
                    <FontAwesomeIcon icon={faWrench} className="worker-tool" />
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="control-panel">
            <FontAwesomeIcon icon={faCog} className={`control-cog cog-1 ${isConveyorBroken ? 'stopped' : ''}`} />
            <FontAwesomeIcon icon={faCog} className={`control-cog cog-2 ${isConveyorBroken ? 'stopped' : ''}`} />
          </div>
        </div>
      </div>

      <nav className={`nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <button className="hamburger-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
          <FontAwesomeIcon icon={mobileMenuOpen ? faTimes : faBars} />
        </button>
        <AnimatePresence>
          {(mobileMenuOpen || (typeof window !== 'undefined' && window.innerWidth > 768)) && (
            <motion.ul initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className={mobileMenuOpen ? 'mobile-menu-open' : ''}>
              <li><a href="#home" onClick={() => setMobileMenuOpen(false)}>{t('nav.home')}</a></li>
              <li><a href="#skills" onClick={() => setMobileMenuOpen(false)}>{t('nav.skills')}</a></li>
              <li><a href="#experience" onClick={() => setMobileMenuOpen(false)}>{t('nav.experience')}</a></li>
              <li><a href="#projects" onClick={() => setMobileMenuOpen(false)}>{t('nav.projects')}</a></li>
              <li><a href="#appointment" onClick={() => setMobileMenuOpen(false)}>{t('nav.contact')}</a></li>
            </motion.ul>
          )}
        </AnimatePresence>
        <LanguageSwitcher />
      </nav>
    </header>
  );
};

export default Header;